import React, { useCallback, useEffect, useMemo, useState } from 'react';

import _times from 'lodash/times';
import _find from 'lodash/find';
import _maxBy from 'lodash/maxBy';
import _get from 'lodash/get';
import _reject from 'lodash/reject';
import _sortBy from 'lodash/sortBy';
import _isNil from 'lodash/isNil';
import classNames from 'classnames';
import {
	CSVUploaderProps,
	CSVUploaderState,
	CsvUploadResult,
	DEFAULT_COLUMNS_SELECTED_TEXT,
	DEFAULT_FINISH_TEXT,
	DEFAULT_SHOW_FIRST_ROW,
	DEFAULT_TOTAL_ROWS_TEXT,
	DEFAULT_UPLOAD_TEXT,
	IAutoCompleteOption
} from '../../utils/csv-uploader.consts';
import { SelectValue } from 'antd/lib/select';
import DraggerView from '../dragger-view/dragger-view';
import ResultsView from '../results-view/results-view';
import { CSVtoArray } from '../../utils/csv-uploader-utils';
import { CSVUploaderModalFooter } from '../csv-uploader-modal-footer/csv-uploader-modal-footer';
import AutoComplete, { AutoCompleteProps } from '../../../auto-complete/auto-complete';
import Table from '../../../table/table';
import { DefaultAutoCompleteProps, DefaultPaginationProps } from '../../utils/default-values';

import './csv-uploader.scss';

const HeaderAutoComplete = (
	options: IAutoCompleteOption[],
	onChange: (value: SelectValue) => void,
	autoCompleteProps: Partial<AutoCompleteProps>,
	defaultValue?: string
) => {
	return (
		<AutoComplete
			{...{ ...DefaultAutoCompleteProps, ...autoCompleteProps }}
			defaultValue={defaultValue}
			options={options}
			onChange={onChange}
		/>
	);
};

const getColumnTitle = (
	options: IAutoCompleteOption[],
	onChange: (value: SelectValue) => void,
	autoCompleteProps: Partial<AutoCompleteProps>,
	defaultValue?: string
) => {
	return _isNil(options) ? defaultValue : HeaderAutoComplete(options, onChange, autoCompleteProps, defaultValue);
};

const CSVUploader = ({
	headers,
	headersOptions,
	onUpload,
	onClose,
	texts,
	autoCompleteProps,
	dragAndDropProps,
	tableProps,
	showFirstRow = DEFAULT_SHOW_FIRST_ROW,
	showDownloadTemplate
}: CSVUploaderProps) => {
	const [componentState, setComponentState] = useState<CSVUploaderState>(CSVUploaderState.DraggerView);
	const [csvFile, setCsvFile] = useState(null);
	const [maxRowLength, setMaxRowLength] = useState(0);
	const [updatedHeaders, setUpdatedHeaders] = useState(headers ? [...headers] : []);
	const [tableData, setTableData] = useState([]);
	const [results, setResults] = useState<CsvUploadResult>(null);

	const getModalTexts = (key: string) => {
		const textsObj = {
			cancelText: texts.cancelText,
			description: texts.description,
			totalRowsText: texts.totalRowsText || DEFAULT_TOTAL_ROWS_TEXT,
			columnsSelectedText: texts.columnsSelectedText || DEFAULT_COLUMNS_SELECTED_TEXT,
			templateFileName: texts.templateFileName,
			downloadTemplateButtonText: texts.downloadTemplateButtonText,
			linesUploadedText: texts.linesUploadedText,
			linesImportedText: texts.linesImportedText,
			linesWithErrorsText: texts.linesWithErrorsText,
			uploadText: texts.uploadText || DEFAULT_UPLOAD_TEXT,
			finishText: texts.finishText || DEFAULT_FINISH_TEXT
		};

		return textsObj[key];
	};

	const [okText, setOkText] = useState(getModalTexts('uploadText'));
	const handleHeaderChange = useCallback(
		index => selectedHeader => {
			if (selectedHeader && _find(headersOptions, { value: selectedHeader })) {
				setUpdatedHeaders(updatedHeaders => [
					...updatedHeaders,
					{
						field_name: selectedHeader,
						field_index: index
					}
				]);
			} else {
				setUpdatedHeaders(updatedHeaders => _reject(updatedHeaders, { field_index: index }));
			}
		},
		[headersOptions]
	);

	const tableColumns = useMemo(() => {
		return _times(maxRowLength, index => {
			const columnSavedHeader = _find(updatedHeaders, { field_index: index + 1 });
			const columnSavedHeaderFieldName = _get(columnSavedHeader, 'field_name');

			return {
				title: getColumnTitle(
					headersOptions,
					handleHeaderChange(index + 1),
					autoCompleteProps,
					columnSavedHeaderFieldName
				),
				dataIndex: index.toString(),
				className: !columnSavedHeaderFieldName && 'unselected-column'
			};
		});
	}, [maxRowLength, updatedHeaders, autoCompleteProps, headersOptions]);

	const mapSerializedCsvToTableRows = useCallback((serializedCsv: string[][]) => {
		return serializedCsv.map((row, rowIndex) => Object.assign({ index: rowIndex }, row));
	}, []);

	const serializeCsvFile = useCallback(async (): Promise<string[][]> => {
		const text = await new Response(csvFile).text();
		const splitByRows = text.split('\n');

		if (!showFirstRow) {
			splitByRows.shift();
		}

		// Split each row to fields
		return splitByRows.map(row => CSVtoArray(row));
	}, [csvFile, showFirstRow]);

	useEffect(() => {
		if (!csvFile) {
			return;
		}

		const showTable = async () => {
			const serializedCsv = await serializeCsvFile();

			setMaxRowLength(
				Math.max(
					_maxBy(serializedCsv, row => row?.length).length,
					_maxBy(headers, header => header.field_index).field_index
				)
			);

			const tableRows = mapSerializedCsvToTableRows(serializedCsv);

			setTableData(tableRows);
		};

		showTable();
	}, [csvFile]);

	const onOkHandler = useCallback(async () => {
		if (componentState === CSVUploaderState.TableView) {
			setComponentState(CSVUploaderState.Uploading);
			const results = await onUpload(_sortBy(updatedHeaders, 'field_index'), csvFile);
			setResults(results);
			setOkText(getModalTexts('finishText'));
			setComponentState(CSVUploaderState.ResultsView);
		}

		if (componentState === CSVUploaderState.ResultsView) {
			onClose();
		}
	}, [updatedHeaders, csvFile, componentState, onUpload]);

	const handleFileUploaded = useCallback((file: Blob) => {
		setCsvFile(file);
		setComponentState(CSVUploaderState.TableView);
	}, []);

	const getTableFooter = useCallback(
		(): JSX.Element => (
			<span>
				{getModalTexts('totalRowsText')} {tableData.length}, {getModalTexts('columnsSelectedText')}{' '}
				{updatedHeaders.length}
			</span>
		),
		[texts.totalRowsText, tableData, texts.columnsSelectedText, updatedHeaders]
	);

	return (
		<div className="csv-uploader">
			{componentState === CSVUploaderState.DraggerView && (
				<DraggerView
					dragAndDropProps={dragAndDropProps}
					onFileUploaded={handleFileUploaded}
					headers={headers}
					showDownloadTemplate={showDownloadTemplate}
					downloadTemplateButtonText={getModalTexts('downloadTemplateButtonText')}
					templateFileName={getModalTexts('templateFileName')}
				/>
			)}
			{(componentState === CSVUploaderState.TableView || componentState === CSVUploaderState.Uploading) && (
				<>
					<span className="csv-description">{texts.description}</span>
					<Table
						{...tableProps}
						columns={tableColumns}
						dataSource={tableData}
						pagination={{ ...DefaultPaginationProps, ...tableProps?.pagination }}
						footer={getTableFooter}
						rowKey="index"
						className={classNames('csv-table', tableProps?.className)}
					/>
				</>
			)}
			{componentState === CSVUploaderState.ResultsView && (
				<ResultsView
					results={results}
					linesImportedText={getModalTexts('linesImportedText')}
					linesUploadedText={getModalTexts('linesUploadedText')}
					linesWithErrorsText={getModalTexts('linesWithErrorsText')}
				/>
			)}
			<CSVUploaderModalFooter
				okButton={{
					text: okText,
					onClick: onOkHandler,
					loading: componentState === CSVUploaderState.Uploading,
					disabled: componentState === CSVUploaderState.DraggerView
				}}
				cancelButton={{
					text: getModalTexts('cancelText'),
					onClick: onClose
				}}
			/>
		</div>
	);
};

export default CSVUploader;
